import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getTokenForRequest } from '../../utils/apiAuth';
import './BbPreview.css';

// Global axios interceptor for 401 Unauthorized
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      // COMMENTED OUT FOR DEBUGGING
      // window.location.href = '/login.aspx';
      console.error('401 Unauthorized - but redirect commented out for debugging');
      return;
    }
    return Promise.reject(error);
  }
);

/**
 * Get cookie value - matches CookieUtilities.getCookie implementation from socrates.js
 * @param {string} c_name - Cookie name
 * @returns {string|undefined} - Cookie value or undefined if not found
 */
const getCookie = function(c_name) {
  var i, x, y, ARRcookies = document.cookie.split(";");
  for (i = 0; i < ARRcookies.length; i++) {
    x = ARRcookies[i].substr(0, ARRcookies[i].indexOf("="));
    y = ARRcookies[i].substr(ARRcookies[i].indexOf("=") + 1);
    x = x.replace(/^\s+|\s+$/g, "");
    if (x == c_name) {
      return unescape(y);
    }
  }
};

/**
 * Helper function to encode token for API calls
 * @param {string} token - The session token
 * @returns {string} - Encoded token
 */
const encodeToken = (token) => {
  return token.toString();
};

/**
 * Helper function to format .NET date string
 * @param {string} dateStr - .NET date string like "/Date(1234567890)/" or "/Date(1234567890+0800)/"
 * @returns {Date} - JavaScript Date object
 */
const parseDotNetDate = (dateStr) => {
  if (!dateStr) return null;
  if (typeof dateStr === 'string' && dateStr.includes('/Date(')) {
    // Match both formats: /Date(1234567890)/ and /Date(1234567890+0800)/
    const match = dateStr.match(/\/Date\((\d+)([+-]\d+)?\)\//);
    if (match) {
      // The timestamp is already in UTC milliseconds, timezone offset is ignored
      return new Date(parseInt(match[1]));
    }
  }
  // If it's already a Date object or number, return as is
  if (dateStr instanceof Date) return dateStr;
  if (typeof dateStr === 'number') return new Date(dateStr);
  return new Date(dateStr);
};

/**
 * Helper function to format date for display
 * @param {Date|string} date - Date object or date string
 * @returns {string} - Formatted date string
 */
const formatDate = (date) => {
  if (!date) return 'N/A';
  const d = date instanceof Date ? date : parseDotNetDate(date);
  if (!d || isNaN(d.getTime())) return 'N/A';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' });
};

/**
 * BbPreview Component
 * Displays Bright Space course preview and allows configuration before syncing
 */
const BbPreview = ({ bbCourseId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [courseInfo, setCourseInfo] = useState(null);
  const [semesters, setSemesters] = useState([]);
  const [selectedSemester, setSelectedSemester] = useState(null);
  const [moduleSemesterTopics, setModuleSemesterTopics] = useState([]);
  const [backupMsTopics, setBackupMsTopics] = useState([]);
  const [selectedLessonPlan, setSelectedLessonPlan] = useState(null);
  const [studentsList, setStudentsList] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [upcomingSync, setUpcomingSync] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msId, setMsId] = useState(null);

  // Load course data on mount
  useEffect(() => {
    if (bbCourseId) {
      loadCourseData();
    } else {
      setError('No Bright Space course ID provided');
      setLoading(false);
    }
  }, [bbCourseId]);

  // Update groups when students list changes
  useEffect(() => {
    if (studentsList && studentsList.length > 0) {
      const uniqueGroups = [];
      studentsList.forEach(student => {
        if (!uniqueGroups.find(g => g.groupId === student.groupId)) {
          uniqueGroups.push({
            groupId: student.groupId,
            groupName: student.groupName
          });
        }
      });
      setGroups(uniqueGroups);
      if (uniqueGroups.length > 0 && !selectedClassId) {
        setSelectedClassId(uniqueGroups[0].groupId);
      }
    }
  }, [studentsList]);

  /**
   * Load Bright Space course data from API
   */
  const loadCourseData = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = getTokenForRequest();

      const response = await axios.get('/webservices/Organization.svc/getbbcourseinfo', {
        params: {
          token: token ? encodeToken(token) : '',
          bbCourseId: bbCourseId
        }
      });

      if (response.data && response.data.success) {
        const data = response.data;
        setCourseInfo(data.bbcourse);
        setSemesters(data.Semesters || []);
        setModuleSemesterTopics(data.ModuleSemesterTopics || []);
        setBackupMsTopics(JSON.parse(JSON.stringify(data.ModuleSemesterTopics || [])));

        // Parse students if available (Students is a JSON string in bbcourse; support both casings)
        const bbcourse = data.bbcourse || {};
        const studentsRaw = bbcourse.Students ?? bbcourse.students;
        if (studentsRaw) {
          try {
            if (typeof studentsRaw === 'string') {
              const trimmed = studentsRaw.trim();
              // Only attempt JSON.parse if it looks like JSON (array or object)
              if (trimmed && (trimmed.startsWith('[') || trimmed.startsWith('{'))) {
                const parsed = JSON.parse(trimmed);
                setStudentsList(Array.isArray(parsed) ? parsed : []);
              } else {
                // Not JSON (e.g. legacy token string) – treat as no students
                setStudentsList([]);
              }
            } else {
              setStudentsList(Array.isArray(studentsRaw) ? studentsRaw : []);
            }
          } catch (e) {
            console.error('Error parsing students:', e);
            setStudentsList([]);
          }
        } else {
          setStudentsList([]);
        }

        // Handle existing module semester topics (for upcoming syncs warning)
        if (data.bbcourse && data.bbcourse.ExistingModuleSemesterTopics && Array.isArray(data.bbcourse.ExistingModuleSemesterTopics)) {
          const upcoming = data.bbcourse.ExistingModuleSemesterTopics.filter(ms => ms.Upcoming > 0);
          setUpcomingSync(upcoming);
        } else {
          setUpcomingSync([]);
        }

        // Set initial semester if available
        if (data.Semesters && data.Semesters.length > 0) {
          // Prefer current or future semesters
          const currentOrFuture = data.Semesters.filter(s => {
            const startDate = parseDotNetDate(s.StartDate);
            const endDate = parseDotNetDate(s.EndDate);
            const now = new Date();
            return startDate && (startDate > now || endDate > now);
          });
          if (currentOrFuture.length > 0) {
            setSelectedSemester(currentOrFuture[0]);
          } else {
            setSelectedSemester(data.Semesters[0]);
          }
        }
      } else {
        setError('Failed to load Bright Space course information');
      }
    } catch (err) {
      console.error('Error loading course data:', err);
      setError(err.response?.data?.ErrorText || 'Failed to load Bright Space course preview');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get module semesters for selected semester
   */
  const getModuleSemestersForSelectedSemester = () => {
    if (!selectedSemester) return [];
    return moduleSemesterTopics.filter(ms => ms.SemesterID === selectedSemester.ID);
  };

  /**
   * Get existing lesson plan for selected semester
   */
  const getExistingLessonPlan = () => {
    if (!courseInfo || !courseInfo.ExistingModuleSemesterTopics || !selectedSemester) return [];
    const existing = courseInfo.ExistingModuleSemesterTopics.find(ms => ms.SemesterID === selectedSemester.ID);
    return existing ? existing.Topics : [];
  };

  /**
   * Validate if lesson plan week is valid for a topic start date
   */
  const lessonPlanWeekValid = (startDate) => {
    if (!selectedSemester || !selectedSemester.LessonPlanWeeks) return false;
    return selectedSemester.LessonPlanWeeks.some(week => week.StartDate === startDate);
  };

  /**
   * Handle lesson plan selection
   */
  const handleSelectLessonPlan = (lessonPlan) => {
    setSelectedLessonPlan(lessonPlan);
    if (!lessonPlan) {
      // Reset to backup if no lesson plan selected
      setModuleSemesterTopics(JSON.parse(JSON.stringify(backupMsTopics)));
    }
  };

  /**
   * Auto-update lesson plan start times when a topic is moved
   */
  const autoUpdateLessonPlanStartTime = (movedTopic, targetWeek) => {
    if (!selectedLessonPlan || !selectedSemester) return;

    const updatedLessonPlan = { ...selectedLessonPlan, changed: true };
    const weeks = selectedSemester.LessonPlanWeeks;
    const weekCount = weeks.length;
    const targetWeekNo = weeks.findIndex(w => w.StartDate === targetWeek.StartDate) + 1;
    const movedWeekNo = weeks.findIndex(w => w.StartDate === movedTopic.StartDate) + 1;

    const updatedTopics = updatedLessonPlan.Topics.map(topic => {
      if (parseInt(topic.ID) === parseInt(movedTopic.ID)) {
        return topic;
      }

      const topicWeek = weeks.find(w => w.StartDate === topic.StartDate);
      if (!topicWeek) return topic;

      const topicWeekNo = weeks.indexOf(topicWeek) + 1;
      if (topicWeekNo < targetWeekNo) {
        return topic;
      }

      // Calculate new week
      let newWeekNo;
      if (targetWeekNo > movedWeekNo) {
        newWeekNo = topicWeekNo - (targetWeekNo - movedWeekNo);
      } else {
        newWeekNo = topicWeekNo + (movedWeekNo - targetWeekNo);
      }

      if (newWeekNo > 0 && newWeekNo <= weekCount) {
        return { ...topic, StartDate: weeks[newWeekNo - 1].StartDate };
      } else if (newWeekNo > weekCount) {
        return { ...topic, StartDate: weeks[weekCount - 1].StartDate };
      }

      return topic;
    });

    setSelectedLessonPlan({ ...updatedLessonPlan, Topics: updatedTopics });
  };

  /**
   * Confirm and save course information
   */
  const handleConfirmCourseInfo = async () => {
    // Step 1 is compulsory - semester must be selected
    if (!selectedSemester) {
      alert('Please select semester (Step 1 is required).');
      return;
    }

    // Step 2 and Step 3 are optional - no validation needed
    // If lesson plan is selected but topics don't have dates, that's okay
    // The backend will handle unmapped topics appropriately

    try {
      setSaving(true);

      // Prepare topics (only include topics that have been mapped to a week)
      // Topics without StartDate or with empty StartDate are optional and can be excluded
      const topics = selectedLessonPlan && selectedLessonPlan.Topics
        ? selectedLessonPlan.Topics
            .filter(topic => topic.StartDate && topic.StartDate !== '') // Only include mapped topics
            .map(topic => ({
              ...topic,
              ModuleSemesterID: selectedLessonPlan.ID
            }))
        : [];

      // Prepare groups/classes
      const bbClasses = groups.map(group => ({
        groupId: group.groupId,
        groupName: group.groupName,
        students: studentsList
          .filter(student => student.groupId === group.groupId)
          .map(student => ({
            groupId: student.groupId,
            userName: student.userName,
            displayName: student.displayName
          }))
      }));

      const token = getTokenForRequest();

      // Show warning if creating new module semester
      if (!msId || msId <= 0) {
        setTimeout(() => {
          alert('This operation may take time to complete. Please wait and try to check class setup later. We will also inform you by email once it is completed.');
        }, 10000);
      }

      const response = await axios.post('/webservices/Organization.svc/savebbcourseinfo', {
        token: token ? encodeToken(token) : '',
        bbCourseName: courseInfo.BbTitle,
        bbCourseCode: courseInfo.BbID,
        bbCourseId: bbCourseId,
        semesterId: selectedSemester.ID,
        lessonPlanWeeks: topics,
        bbClasses: bbClasses
      });

      if (response.data && response.data > 0) {
        const newMsId = response.data;
        setMsId(newMsId);

        // Update coordinated module semesters cookie
        if (window.CookieUtilities) {
          const chelper = new window.CookieUtilities();
          const existing = chelper.getCookie('coordinatedModuleSemesters') || '';
          const exdate = new Date();
          exdate.setDate(exdate.getDate() + 30);
          chelper.setCookie('coordinatedModuleSemesters', existing ? `${existing},${newMsId}` : String(newMsId), exdate);
        }

        // Show success message
        const goToModuleAdmin = confirm('Course info successfully saved! Remember to go to module admin to add class educators.\n\nWould you like to go to Module Admin now?');
        if (goToModuleAdmin) {
          window.location.href = '/Index.aspx#ModuleAdmin';
        }
      } else {
        alert('Failed to save course info, please contact support.');
      }
    } catch (err) {
      console.error('Error saving course info:', err);
      alert(err.response?.data?.ErrorText || 'Failed to save course info due to network issue, please contact support.');
    } finally {
      setSaving(false);
    }
  };

  // Filter students by selected class/group
  const filteredStudents = selectedClassId
    ? studentsList.filter(s => s.groupId === selectedClassId)
    : studentsList;

  // Get available lesson plans for selected semester
  const availableLessonPlans = getModuleSemestersForSelectedSemester();

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading Bright Space course information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error</h4>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={loadCourseData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!courseInfo) {
    return (
      <div className="container mt-4">
        <div className="alert alert-warning" role="alert">
          No course information available.
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-12">
          <h2 className="mb-4">Bright Space Course Preview</h2>
        </div>
      </div>

      {/* Course Information */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Course Information</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <p><strong>Course Title:</strong> {courseInfo.BbTitle || 'N/A'}</p>
                </div>
                <div className="col-md-6">
                  <p><strong>Course ID:</strong> {courseInfo.BbID || 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Upcoming Sync Warning */}
      {upcomingSync.length > 0 && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-warning" role="alert">
              <h6 className="alert-heading">Upcoming Syncs</h6>
              <p>There are {upcomingSync.length} module semester(s) with upcoming syncs.</p>
            </div>
          </div>
        </div>
      )}

      {/* Step 1: Semester Selection - COMPULSORY */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Step 1: Select Semester <span className="text-danger">*</span></h5>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <label htmlFor="semesterSelect" className="form-label">Semester <span className="text-danger">*</span></label>
                <select
                  id="semesterSelect"
                  className="form-select"
                  value={selectedSemester?.ID || ''}
                  onChange={(e) => {
                    const semester = semesters.find(s => s.ID === parseInt(e.target.value));
                    setSelectedSemester(semester);
                    setSelectedLessonPlan(null);
                  }}
                  required
                >
                  <option value="">-- Select Semester --</option>
                  {semesters.map(semester => (
                    <option key={semester.ID} value={semester.ID}>
                      {semester.Name} ({formatDate(semester.StartDate)} - {formatDate(semester.EndDate)})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Step 2: Lesson Plan Selection - OPTIONAL */}
      {selectedSemester && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">Step 2: Select Lesson Plan <span className="text-muted small">(Optional)</span></h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label htmlFor="lessonPlanSelect" className="form-label">Lesson Plan</label>
                  <select
                    id="lessonPlanSelect"
                    className="form-select"
                    value={selectedLessonPlan?.ID || ''}
                    onChange={(e) => {
                      const lessonPlan = availableLessonPlans.find(lp => lp.ID === parseInt(e.target.value));
                      handleSelectLessonPlan(lessonPlan);
                    }}
                  >
                    <option value="">-- Select Lesson Plan (Optional) --</option>
                    {availableLessonPlans.map(lp => (
                      <option key={lp.ID} value={lp.ID}>
                        {lp.Module || lp.ModuleName || 'Module'} - {lp.Semester || 'Semester'} ({lp.Topics ? lp.Topics.length : 0} topics)
                      </option>
                    ))}
                  </select>
                </div>

                {/* Step 3: Lesson Plan Topics - Display Only (Not Selectable) */}
                {selectedLessonPlan && selectedLessonPlan.Topics && (
                  <div className="mt-3">
                    <h6>Step 3: Topic Start Dates <span className="text-muted small">(Display Only)</span></h6>
                    <p className="text-muted small mb-2">Topic start dates are shown for reference. Week mapping is not editable.</p>
                    <div className="table-responsive">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>Topic</th>
                            <th>Start Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedLessonPlan.Topics.map((topic, index) => {
                            const week = selectedSemester.LessonPlanWeeks?.find(w => w.StartDate === topic.StartDate);
                            const weekDisplay = week 
                              ? `Week ${week.WeekNo}: ${formatDate(week.StartDate)}`
                              : (topic.StartDate ? formatDate(topic.StartDate) : 'Not Mapped');
                            return (
                              <tr key={topic.ID || index}>
                                <td>{topic.TopicName || topic.Desc || 'N/A'}</td>
                                <td>
                                  <span className="text-muted">{weekDisplay}</span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Students and Groups - always show card */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Students and Groups</h5>
            </div>
            <div className="card-body">
              {studentsList.length === 0 ? (
                <p className="text-muted mb-0">
                  No students in this course. The student list will appear here after the course is synced from Bright Space, or when students are added to the course.
                </p>
              ) : (
                <>
                  {/* Group Selection */}
                  {groups.length > 0 && (
                    <div className="mb-3">
                      <label htmlFor="groupSelect" className="form-label">Filter by Group</label>
                      <select
                        id="groupSelect"
                        className="form-select"
                        value={selectedClassId || ''}
                        onChange={(e) => setSelectedClassId(e.target.value)}
                      >
                        <option value="">All Groups</option>
                        {groups.map(group => (
                          <option key={group.groupId} value={group.groupId}>
                            {group.groupName}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Students Table */}
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Group</th>
                          <th>Username</th>
                          <th>Display Name</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStudents.map((student, index) => (
                          <tr key={index}>
                            <td>{student.groupName || 'N/A'}</td>
                            <td>{student.userName || 'N/A'}</td>
                            <td>{student.displayName || 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-muted small">
                    Total: {filteredStudents.length} student(s)
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-end gap-2">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => window.location.href = '/Index.aspx#Dashboard'}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleConfirmCourseInfo}
              disabled={saving || !selectedSemester}
              id="saveCourseInfo"
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Saving...
                </>
              ) : (
                'Save Course Information'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BbPreview;
